import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLanguage } from '../context/LanguageContext';
import './ProjectDetails.css';

// Set the worker source for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(false);

  const projectIndex = parseInt(id, 10);
  const project = t.projects.items[projectIndex];

  // Get other projects for recommendations (exclude current project)
  const otherProjects = t.projects.items
    .map((proj, idx) => ({ ...proj, index: idx }))
    .filter((_, idx) => idx !== projectIndex)
    .slice(0, 5); // Show up to 5 recommendations

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setPdfError(true);
  };

  const handleProjectClick = (index) => {
    const targetProject = t.projects.items[index];
    if (targetProject && targetProject.canOpenDetails) {
      navigate(`/project/${index}`);
      window.scrollTo(0, 0);
    }
  };

  // Extract YouTube video ID from various URL formats
  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (!project) {
    return (
      <div className="project-details-page">
        <div className="project-not-found">
          <h2>{isRTL ? 'المشروع غير موجود' : 'Project Not Found'}</h2>
          <button onClick={() => navigate('/')} className="back-button">
            {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  // Check if project can be opened in details
  if (!project.canOpenDetails) {
    navigate('/');
    return null;
  }

  return (
    <div className="project-details-page">
      <div className="project-details-container">
        <button onClick={() => navigate('/')} className="back-button">
          <span className="back-arrow">{isRTL ? '→' : '←'}</span>
          {isRTL ? 'العودة للمشاريع' : 'Back to Projects'}
        </button>

        <div className="project-main-layout">
          {/* Main Content */}
          <div className="project-main-content">
            <div className="project-details-content">
              <div className="project-media-section">
                <div className="project-details-image">
                  <img src={project.image} alt={project.title} />
                  {project.category && (
                    <div className="project-details-category">{project.category}</div>
                  )}
                </div>

                {/* Facebook Page Section */}
                <div className="facebook-page-section">
                  <h3 className="facebook-section-title">
                    {isRTL ? 'صفحتنا على فيس بوك' : 'Our Facebook Page'}
                  </h3>
                  <div className="facebook-page-container">
                    <iframe
                      title={isRTL ? 'صفحة فيس بوك' : 'Facebook Page'}
                      src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fdr.adhamelsharkaawy%2F&tabs&width=340&height=130&small_header=false&adapt_container_width=false&hide_cover=false&show_facepile=false&appId=1836251596668056"
                      width="340"
                      height="130"
                      style={{ border: 'none', overflow: 'hidden', background: 'transparent' }}
                      scrolling="no"
                      frameBorder="0"
                      allowFullScreen={true}
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      allowTransparency="true"
                    ></iframe>
                  </div>
                </div>
              </div>

              <div className="project-details-info">
                <h1 className="project-details-title">{project.title}</h1>
                <p className="project-details-description">{project.description}</p>

                {project.details && (
                  <div className="project-details-extra">
                    <h3>{isRTL ? 'تفاصيل المشروع' : 'Project Details'}</h3>
                    <p>{project.details}</p>
                  </div>
                )}

                {project.copyright && (
                  <div className="project-copyright-box">
                    <h3>{isRTL ? 'حقوق الملكية' : 'Copyrights'}</h3>
                    <div className="copyright-content">
                      <i className="fa fa-shield"></i>
                      <p>{project.copyright}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Videos Section */}
            {project.videos && project.videos.length > 0 && (
              <div className="project-videos-section">
                <h3 className="videos-section-title">
                  {isRTL ? 'فيديوهات المشروع' : 'Project Videos'}
                </h3>
                <div className="videos-grid">
                  {project.videos.map((video, index) => {
                    const videoId = getYouTubeId(video.url);
                    return videoId ? (
                      <div key={index} className="video-item">
                        <div className="video-wrapper">
                          <iframe
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={video.title || `Video ${index + 1}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        </div>
                        {video.title && <p className="video-title">{video.title}</p>}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Images Section */}
            {project.images && project.images.length > 0 && (
              <div className="project-images-section">
                <h3 className="images-section-title">
                  {isRTL ? 'صور المشروع' : 'Project Images'}
                </h3>
                <div className="images-grid">
                  {project.images.map((image, index) => (
                    <div key={index} className="image-item">
                      <img src={image.src} alt={image.title || `Image ${index + 1}`} />
                      {image.title && <p className="image-caption">{image.title}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PDF Gallery Section */}
            {project.pdf && (
              <div className="project-pdf-section">
                <h3 className="pdf-section-title">
                  {isRTL ? 'تفاصيل المشروع' : 'Project Information'}
                </h3>
                {pdfError ? (
                  <div className="pdf-error">
                    <p>{isRTL ? 'تعذر تحميل ملف PDF' : 'Failed to load PDF file'}</p>
                  </div>
                ) : (
                  <div className="pdf-pages-grid">
                    <Document
                      file={project.pdf}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <div className="pdf-loading">
                          {isRTL ? 'جاري تحميل المستند...' : 'Loading document...'}
                        </div>
                      }
                    >
                      {numPages && Array.from(new Array(numPages), (el, index) => (
                        <div key={`page_${index + 1}`} className="pdf-page-wrapper">
                          <Page
                            pageNumber={index + 1}
                            width={700}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                          <span className="pdf-page-number">
                            {isRTL ? `صفحة ${index + 1}` : `Page ${index + 1}`}
                          </span>
                        </div>
                      ))}
                    </Document>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Other Projects */}
          <div className="project-sidebar">
            <h3 className="sidebar-title">
              {isRTL ? 'مشاريع أخرى' : 'Other Projects'}
            </h3>
            <div className="sidebar-projects">
              {otherProjects.map((proj) => (
                <div
                  key={proj.index}
                  className={`sidebar-project-card ${!proj.canOpenDetails ? 'disabled' : ''}`}
                  onClick={() => handleProjectClick(proj.index)}
                  style={{ cursor: proj.canOpenDetails ? 'pointer' : 'not-allowed' }}
                >
                  <div className="sidebar-project-image">
                    <img src={proj.image} alt={proj.title} />
                  </div>
                  <div className="sidebar-project-info">
                    <h4>{proj.title}</h4>
                    {/*<p>{proj.description.substring(0, 60)}...</p>*/}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media Links */}
            <div className="sidebar-social-media">
              <h4 className="social-title">
                {isRTL ? 'للمتابعة' : 'Follow Us'}
              </h4>
              <div className="social-links">
                <a
                  href={t.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link facebook"
                  title="Facebook"
                >
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a
                  href={t.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link twitter"
                  title="Twitter"
                >
                  <i className="fab fa-twitter"></i>
                </a>
                <a
                  href={t.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link instagram"
                  title="Instagram"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href={t.socialMedia.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link linkedin"
                  title="LinkedIn"
                >
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a
                  href={t.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link youtube"
                  title="YouTube"
                >
                  <i className="fab fa-youtube"></i>
                </a>
                <a
                  href={t.socialMedia.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link whatsapp"
                  title="WhatsApp"
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
